import React, { useEffect, useId, useRef } from 'react';
import themeStyles from '../photoEditorTheme.module.css';

interface CloseConfirmProps {
  onKeepEditing: () => void;
  onConfirmClose: () => void;
}

/**
 * Asks before the editor closes and throws away the photo being edited. It sits over the editor,
 * inside its dialog, and uses the editor footer's Cancel / Save buttons. Escape is the editor's to
 * handle: pressed again here, it keeps editing.
 *
 * @param props - Component props.
 * @param props.onKeepEditing - Dismisses the question and leaves the editor as it was.
 * @param props.onConfirmClose - Closes the editor, as its Cancel does.
 * @returns The confirmation.
 */
export function CloseConfirm({ onKeepEditing, onConfirmClose }: CloseConfirmProps) {
  const id = useId();
  const keepEditingRef = useRef<HTMLButtonElement | null>(null);

  // "Keep editing", the safe answer, takes focus first. Dismissing the question puts focus back
  // where it was, unless that went with the editor.
  useEffect(() => {
    const previousFocus = document.activeElement;
    keepEditingRef.current?.focus();
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  // Tab and Shift+Tab cycle the two buttons.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button');
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  // A press on the backdrop or the panel itself would otherwise move focus out to the editor.
  const keepFocus = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element) || !event.target.closest('button')) event.preventDefault();
  };

  return (
    <div className={themeStyles['photo-editor__confirm']} onMouseDown={keepFocus}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        className={themeStyles['photo-editor__confirm-panel']}
        onKeyDown={handleKeyDown}
      >
        <h2 id={`${id}-title`} className="heading-xl">
          Are you sure you want to close?
        </h2>
        <p id={`${id}-description`} className="body-base">
          Your changes to this photo will be lost.
        </p>
        <div className={themeStyles['photo-editor__modal-footer-actions']}>
          <button
            type="button"
            className={themeStyles['photo-editor__modal-footer-cancel'] + ' button button--secondary button--black'}
            onClick={onConfirmClose}
          >
            Yes, close
          </button>
          <button
            ref={keepEditingRef}
            type="button"
            className={themeStyles['photo-editor__modal-footer-save'] + ' button button--primary button--orange'}
            onClick={onKeepEditing}
          >
            Keep editing
          </button>
        </div>
      </div>
    </div>
  );
}
